import { createSlice } from '@reduxjs/toolkit'
import { sortBy } from 'es-toolkit'

import * as API from 'api/dashboard/dashboard'
import type {
  ScheduleTypeSummaryResponse,
  TenantBopMonitoringParam,
  TenantBopMonitoringResponse,
  TenantSummaryFilteringData,
  TenantSummaryResponse,
  WorkerCountsResponse,
  WorkerCountWorkspaceData,
  WorkspaceSummaryResponse,
} from 'api/dashboard/types'

import { handleApiError } from './utils'

import type { PayloadAction } from '@reduxjs/toolkit'
import type { AxiosError } from 'axios'
import type { AppThunk, RootState } from 'store'

type ScheduleTypeSummaryData = ScheduleTypeSummaryResponse & { scheduleTypeId: number }

type DashboardState = {
  isRequesting: boolean
  errorMessage: string
  tenantSummary?: TenantSummaryResponse
  workspaceSummary?: WorkspaceSummaryResponse
  bopMonitoring?: TenantBopMonitoringResponse
  workerCounts: WorkerCountWorkspaceData[]
  scheduleTypeSummary: ScheduleTypeSummaryData[]
}

const initialState: DashboardState = {
  isRequesting: false,
  errorMessage: '',
  workerCounts: [],
  scheduleTypeSummary: [],
}

export const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    startRequest: state => {
      state.isRequesting = true
      state.errorMessage = ''
    },
    clearErrorMessage: state => {
      state.errorMessage = ''
    },
    apiFailure: (state, action: PayloadAction<{ errorMessage: string }>) => {
      state.isRequesting = false
      state.errorMessage = action.payload.errorMessage
    },

    getTenantSummarySuccess: (state, action: PayloadAction<TenantSummaryResponse>) => {
      state.isRequesting = false
      if (!state.tenantSummary) {
        state.tenantSummary = action.payload
      }

      const responseHourlyData = action.payload.hourlyWorkData

      // 先頭データの日付を比較
      const currentDate = state.tenantSummary.hourlyWorkData[0]?.data[0]?.time
      const responseDate = responseHourlyData[0]?.data[0]?.time
      const hourlyWorkData =
        currentDate === responseDate
          ? responseHourlyData.reduce((acc, cur) => {
              if (acc.some(hwd => hwd.scheduleTypeId === cur.scheduleTypeId)) {
                return acc.map(data => (data.scheduleTypeId === cur.scheduleTypeId ? cur : data))
              }
              acc.push(cur)
              return acc
            }, state.tenantSummary.hourlyWorkData)
          : responseHourlyData

      // ワークスペースの編集によりデータ変更された場合にstate.tenantSummary.workspaceDataを更新するための変数
      const updateWorkspaceData = action.payload.workspaceData.map(wd => {
        const target = state.tenantSummary?.workspaceData.find(w => w.workspaceId === wd.workspaceId)
        if (!target) {
          return wd
        }

        const data = wd.data.map(d => {
          const targetData = target.data.find(td => td.scheduleTypeId === d.scheduleTypeId)
          if (!targetData) {
            return d
          }
          return { ...targetData, scheduleTypeName: d.scheduleTypeName }
        })
        return { data, workspaceId: wd.workspaceId, workspaceName: wd.workspaceName }
      })

      const updateScheduleTypeIds = action.payload.hourlyWorkData.map(hwd => hwd.scheduleTypeId)
      const workspaceData = updateWorkspaceData.map(wd => {
        const data = wd.data.map(d =>
          updateScheduleTypeIds.includes(d.scheduleTypeId)
            ? action.payload.workspaceData.flatMap(w => w.data).find(w => w.scheduleTypeId === d.scheduleTypeId) || d
            : d
        )
        return { ...wd, data }
      })
      state.tenantSummary = { hourlyWorkData, workspaceData }
    },
    getWorkspaceSummarySuccess: (state, action: PayloadAction<WorkspaceSummaryResponse>) => {
      state.isRequesting = false
      state.workspaceSummary = action.payload
    },

    clearTenantSummary: state => (state.tenantSummary = undefined),
    getBopMonitoringSummarySuccess: (state, action: PayloadAction<TenantBopMonitoringResponse>) => {
      state.isRequesting = false
      state.bopMonitoring = action.payload
    },
    getWorkerCountsSuccess: (state, action: PayloadAction<WorkerCountsResponse>) => {
      state.isRequesting = false
      state.workerCounts = sortBy(action.payload.workspaceData, ['workspaceName']).map(workspace => ({
        ...workspace,
        planSummary: sortBy(workspace.planSummary, ['scheduleTypeName']),
      }))
    },
    getScheduleTypeSummarySuccess: (state, action: PayloadAction<ScheduleTypeSummaryData>) => {
      state.isRequesting = false
      const index = state.scheduleTypeSummary.findIndex(s => s.scheduleTypeId === action.payload.scheduleTypeId)
      index >= 0
        ? state.scheduleTypeSummary.splice(index, 1, action.payload)
        : state.scheduleTypeSummary.push(action.payload)
    },
  },
})

export const {
  startRequest,
  clearErrorMessage,
  apiFailure,
  getTenantSummarySuccess,
  getWorkspaceSummarySuccess,
  clearTenantSummary,
  getBopMonitoringSummarySuccess,
  getWorkerCountsSuccess,
  getScheduleTypeSummarySuccess,
} = dashboardSlice.actions

export const getTenantSummary =
  (date: string, filteringData: TenantSummaryFilteringData): AppThunk =>
  async dispatch => {
    dispatch(startRequest())

    try {
      const res = await API.getTenantSummary(date, filteringData)
      dispatch(getTenantSummarySuccess(res))
    } catch (res) {
      handleApiError(res as AxiosError, dispatch, apiFailure)
    }
  }

export const getWorkspaceSummary =
  (workspaceId: number, date: string): AppThunk =>
  async dispatch => {
    dispatch(startRequest())

    try {
      const res = await API.getWorkspaceSummary(workspaceId, date)
      dispatch(getWorkspaceSummarySuccess(res))
    } catch (res) {
      handleApiError(res as AxiosError, dispatch, apiFailure)
    }
  }

export const getBopMonitoringSummary =
  (date: string, data: TenantBopMonitoringParam): AppThunk =>
  async dispatch => {
    dispatch(startRequest())

    try {
      const res = await API.getBopMonitoring(date, data)
      dispatch(getBopMonitoringSummarySuccess(res))
    } catch (res) {
      handleApiError(res as AxiosError, dispatch, apiFailure)
    }
  }

export const getWorkerCounts =
  (date: string, workspaceId?: number): AppThunk =>
  async dispatch => {
    dispatch(startRequest())

    try {
      const res = await API.getWorkerCounts(date, workspaceId)
      dispatch(getWorkerCountsSuccess(res))
    } catch (res) {
      handleApiError(res as AxiosError, dispatch, apiFailure)
    }
  }

export const getScheduleTypeSummary =
  (workspaceId: number, date: string, scheduleTypeId: number): AppThunk =>
  async dispatch => {
    dispatch(startRequest())

    try {
      const res = await API.getScheduleTypeSummary(workspaceId, date, scheduleTypeId)
      dispatch(getScheduleTypeSummarySuccess({ ...res, scheduleTypeId }))
    } catch (res) {
      handleApiError(res as AxiosError, dispatch, apiFailure)
    }
  }

export const selectDashboardStatus = (state: RootState) => ({ ...state.dashboard })

export default dashboardSlice.reducer
